/**
 * MHPanel - Mindhunters Governance-Compliant Panel Component
 *
 * Layer 3: MH Components (Governance Layer)
 * - Two variants: soft (spacious) and dense (compact)
 * - Used for elevated surfaces in OBSERVE/ANALYZE states
 *
 * Usage:
 * - soft: Auth panels, feature showcases (generous padding)
 * - dense: Data displays, compact layouts (tight padding)
 */

'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const mhPanelVariants = cva(
  'rounded-2xl bg-surface-1',
  {
    variants: {
      variant: {
        soft: 'p-8', // 32px padding
        dense: 'p-6', // 24px padding
      },
    },
    defaultVariants: {
      variant: 'soft',
    },
  }
);

export interface MHPanelProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mhPanelVariants> {
  interactive?: boolean;
}

const MHPanel = React.forwardRef<HTMLDivElement, MHPanelProps>(
  ({ className, variant, interactive = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          mhPanelVariants({ variant }),
          className,
          interactive && 'mh-interactive'
        )}
        {...props}
      />
    );
  }
);

MHPanel.displayName = 'MHPanel';

export { MHPanel, mhPanelVariants };
